"use client";

import { TCandidatesConfig } from "@/app/config";
import { User, Building2, Target } from "lucide-react";
import Link from "next/link";
import React from "react";
import { motion } from "motion/react";

const featuresIcons = [
  {
    icon: User,
  },
  {
    icon: Building2,
  },
  {
    icon: Target,
  },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

export default function HeroCandidate({
  config,
}: {
  config: TCandidatesConfig["hero"];
}) {
  return (
    <section className="relative py-16 lg:py-24 bg-white flex flex-col w-full items-center overflow-hidden">
      {/* Animated decorative blur elements */}
      <motion.div
        className="absolute w-[252px] h-[252px] top-[70px] left-[35px] bg-[#FFD6A7]/40 rounded-full blur-[32px]"
        variants={{
          hidden: { opacity: 0, scale: 0.8 },
          visible: {
            opacity: 1,
            scale: 1,
            transition: {
              duration: 1.2,
              ease: "easeOut",
            },
          },
        }}
        initial="hidden"
        animate="visible"
      />
      <motion.div
        className="absolute w-[336px] h-[336px] bottom-[70px] right-[35px] bg-[#FFD6A7]/40 rounded-full blur-[32px]"
        variants={{
          hidden: { opacity: 0, scale: 0.8 },
          visible: {
            opacity: 1,
            scale: 1,
            transition: {
              duration: 1.2,
              ease: "easeOut",
            },
          },
        }}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.3 }}
      />

      <div className="max-w-5xl w-full px-4 md:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          className="flex flex-col w-full items-center gap-6 mb-12"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Badge */}
          <motion.div
            className="inline-flex items-center px-3 py-1 bg-white rounded-full border backdrop-blur-sm backdrop-brightness-[100%] [-webkit-backdrop-filter:blur(4px)_brightness(100%)]"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  ease: "easeOut",
                },
              },
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="text-[#354152] text-sm font-medium">
              {config.tag}
            </span>
          </motion.div>

          {/* Main Headline */}
          <div className="text-center">
            <motion.h1
              className="text-[#495565] text-4xl lg:text-5xl font-normal leading-tight mb-2 [font-family:'Segoe_UI_Symbol-Regular',Helvetica]"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.8,
                    ease: "easeOut",
                  },
                },
              }}
              initial="hidden"
              animate="visible"
            >
              {config.title}
            </motion.h1>
            <motion.h2
              className="text-[#ffbf00] text-4xl lg:text-5xl font-normal leading-tight [font-family:'Segoe_UI_Symbol-Regular',Helvetica]"
              variants={{
                hidden: { opacity: 0, y: 30, scale: 0.95 },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.8,
                    delay: 0.2,
                    ease: "easeOut",
                  },
                },
              }}
              initial="hidden"
              animate="visible"
            >
              {config.highlighted_title}
            </motion.h2>
          </div>

          {/* Subtitle */}
          <motion.p
            className="text-[#495565] text-lg text-center max-w-2xl leading-relaxed [font-family:'Segoe_UI_Symbol-Regular',Helvetica]"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  ease: "easeOut",
                },
              },
            }}
          >
            {config.description}
          </motion.p>
        </motion.div>

        {/* Feature Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {config.features.map((feature, index) => {
            const Icons = featuresIcons[index].icon;
            return (
              <motion.div
                key={index}
                className="bg-white hover:bg-yellow-50 rounded-[12.75px] p-6 border backdrop-blur-sm backdrop-brightness-[100%] [-webkit-backdrop-filter:blur(4px)_brightness(100%)] hover:shadow-md transition-colors duration-300"
                variants={{
                  hidden: { opacity: 0, y: 30, scale: 0.95 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    transition: {
                      duration: 0.6,
                      ease: "easeOut",
                    },
                  },
                  hover: {
                    y: -5,
                    scale: 1.02,
                    transition: {
                      duration: 0.2,
                      ease: "easeInOut",
                    },
                  },
                }}
                whileHover="hover"
                whileTap={{ scale: 0.98 }}
              >
                {/* Icon */}
                <motion.div
                  className={`w-12 h-12 ${feature.bgColor} rounded-[12.75px] flex items-center justify-center mb-4`}
                  whileHover={{ rotate: 5, scale: 1.1 }}
                  transition={{ duration: 0.2 }}
                >
                  <Icons className={`w-6 h-6 ${feature.iconColor}`} />
                </motion.div>

                {/* Content */}
                <div className="space-y-3">
                  <h3 className="text-[#495565] text-lg font-normal leading-tight [font-family:'Segoe_UI_Symbol-Regular',Helvetica]">
                    {feature.title}
                  </h3>
                  <p className="text-[#495565] text-sm leading-relaxed [font-family:'Segoe_UI_Symbol-Regular',Helvetica]">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* CTA Button */}
        <motion.div
          className="flex justify-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Link href={config.button.href}>
            <motion.button
              className="bg-[#ffbf00] hover:bg-[#ffbf00]/90 text-white px-8 py-3 rounded-[12.75px] font-normal text-lg transition-colors duration-200 shadow-lg hover:shadow-xl [font-family:'Segoe_UI_Symbol-Regular',Helvetica]"
              variants={{
                hidden: { opacity: 0, scale: 0.9 },
                visible: {
                  opacity: 1,
                  scale: 1,
                  transition: {
                    duration: 0.5,
                    ease: "easeOut",
                  },
                },
                hover: {
                  scale: 1.05,
                  transition: {
                    duration: 0.2,
                    ease: "easeInOut",
                  },
                },
                tap: {
                  scale: 0.98,
                },
              }}
              whileHover="hover"
              whileTap="tap"
            >
              {config.button.title}
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
